import type { LonLat } from "utils/curveLine";
import { kmTrimmed, strings } from "./i18n";
import type { Lang, PartnerMapContent, PartnerTheme, Place } from "./types";

/** A place of the map with its distance from the base. */
export interface MappedPlace extends Place {
	kind: "highlight" | "venue";
	/** straight-line km from the base */
	km: number;
	point: LonLat;
}

/** What a place is decides its colour, in the list and on the map alike: the partner's own picks wear its primary, a venue of the week program its accent. */
export const placeFill = (theme: PartnerTheme, kind: MappedPlace["kind"]): string => (kind === "highlight" ? theme.primary : theme.accent);

/** The light look of a hostel's map (Urban Jungle): neither Mad Monkey's lit one nor In de Waag's venue one. Its phone layout is Ilse's prototype of 21 Oct. */
export const hostelLook = (theme: PartnerTheme): boolean => !theme.lit && theme.look !== "venue";

const EARTH_RADIUS_KM = 6371;
const rad = (deg: number) => (deg * Math.PI) / 180;

/** Great-circle distance between two points, in km. */
export function distanceKm(a: LonLat, b: LonLat): number {
	const dLat = rad(b[1] - a[1]);
	const dLon = rad(b[0] - a[0]);
	const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a[1])) * Math.cos(rad(b[1])) * Math.sin(dLon / 2) ** 2;
	return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h));
}

const COMPASS = ["north", "north-east", "east", "south-east", "south", "south-west", "west", "north-west"];

/** The way from `a` to `b` as one of the eight compass words: "north", "south-east". */
export function compassWord(a: LonLat, b: LonLat): string {
	const dLon = rad(b[0] - a[0]);
	const y = Math.sin(dLon) * Math.cos(rad(b[1]));
	const x = Math.cos(rad(a[1])) * Math.sin(rad(b[1])) - Math.sin(rad(a[1])) * Math.cos(rad(b[1])) * Math.cos(dLon);
	const bearing = ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
	return COMPASS[Math.round(bearing / 45) % 8];
}

/** a steady walking pace, 75 m a minute */
export const walkMinutes = (km: number): number => Math.max(1, Math.round((km * 1000) / 75));

/** a steady cycling pace, 15 km/h, and two minutes to get going; never under 3 */
export const bikeMinutes = (km: number): number => Math.max(3, Math.round(km * 4 + 2));

/** Past the content's `bikeFromKm` (in a straight line from the base) a place is a bike ride, not a walk. */
export const isBike = (km: number, bikeFromKm?: number): boolean => bikeFromKm !== undefined && km > bikeFromKm;

/** The minutes from the base: on foot, or by bike past `bikeFromKm`. */
export const travelMinutes = (km: number, bikeFromKm?: number): number => (isBike(km, bikeFromKm) ? bikeMinutes(km) : walkMinutes(km));

/** For a partner with a `rideLabel`: a place this far from the base, in a straight line, is a walk; a farther one a ride */
export const WALK_MAX_KM = 2;

/** "4 min walk", or past a half hour's walk "2.4 km away"; with a `ride` ("by Grab"), a place beyond `WALK_MAX_KM` is "2.5 km by Grab"; past `bikeFromKm`, "12 min by bike". In the page's language: "4 min lopen", "2,4 km verderop" */
export const walkLabel = (km: number, ride?: string, lang: Lang = "en", bikeFromKm?: number): string => {
	const t = strings(lang);
	if (isBike(km, bikeFromKm)) return t.minBike(bikeMinutes(km));
	if (ride === undefined) return km <= 2.2 ? t.minWalk(walkMinutes(km)) : t.kmAway(kmTrimmed(km, lang));
	return km <= WALK_MAX_KM ? t.minWalk(walkMinutes(km)) : `${kmTrimmed(km, lang)} km ${ride}`;
};

/** Every highlight and venue by slug, with its distance from the base: computed from the coordinates, not typed in twice. */
export function indexPlaces(content: PartnerMapContent): Record<string, MappedPlace> {
	const base: LonLat = [content.base.lng, content.base.lat];
	const mapped =
		(kind: MappedPlace["kind"]) =>
		(p: Place): [string, MappedPlace] => {
			const point: LonLat = [p.lng, p.lat];
			return [p.slug, { ...p, kind, point, km: distanceKm(base, point) }];
		};
	return Object.fromEntries([...content.highlights.map(mapped("highlight")), ...content.venues.map(mapped("venue"))]);
}
